import React, { useRef, useEffect } from 'react';
import { ItemType } from '@openai/realtime-api-beta/dist/lib/client.js';
import { WavRenderer } from '../../utils/wav_renderer';
import RecordButton from '../recordeButton';
import InlineTranscript from '../InlineTranscript/InlineTranscript';

interface RecordingSectionProps {
  status: 'idle' | 'recording';
  setStatus: React.Dispatch<React.SetStateAction<'idle' | 'recording'>>;
  isConnected: boolean;
  isLoading: boolean;
  load: boolean;
  topProperties: any;
  wavRecorderRef: React.RefObject<any>;
  wavStreamPlayerRef: React.RefObject<any>;
  startRecording: () => void;
  stopRecording: (send: boolean) => void;
  items: ItemType[];
  isAnyModalOpen: boolean;
}

const RecordingSection: React.FC<RecordingSectionProps> = ({
  status,
  setStatus,
  isConnected,
  isLoading,
  load,
  topProperties,
  wavRecorderRef,
  wavStreamPlayerRef,
  startRecording,
  stopRecording,
  items,
  isAnyModalOpen,
}) => {
  const clientCanvasRef = useRef<HTMLCanvasElement>(null);
  const serverCanvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    let isLoaded = true;
    let frameId: number;
    const wavRecorder = wavRecorderRef.current;
    const clientCanvas = clientCanvasRef.current;
    let clientCtx: CanvasRenderingContext2D | null = null;

    const wavStreamPlayer = wavStreamPlayerRef.current;
    const serverCanvas = serverCanvasRef.current;
    let serverCtx: CanvasRenderingContext2D | null = null;

    const render = () => {
      if (isLoaded) {
        if (clientCanvas) {
          if (!clientCanvas.width || !clientCanvas.height) {
            clientCanvas.width = clientCanvas.offsetWidth;
            clientCanvas.height = clientCanvas.offsetHeight;
          }
          clientCtx = clientCtx || clientCanvas.getContext('2d');
          if (clientCtx) {
            clientCtx.clearRect(0, 0, clientCanvas.width, clientCanvas.height);
            const result = wavRecorder.recording
              ? wavRecorder.getFrequencies('voice')
              : { values: new Float32Array([0]) };
            WavRenderer.drawBars(
              clientCanvas,
              clientCtx,
              result.values,
              'rgba(218, 41, 90, 1)',
              10,
              0,
              8
            );
          }
        }
        if (serverCanvas) {
          if (!serverCanvas.width || !serverCanvas.height) {
            serverCanvas.width = serverCanvas.offsetWidth;
            serverCanvas.height = serverCanvas.offsetHeight;
          }
          serverCtx = serverCtx || serverCanvas.getContext('2d');
          if (serverCtx) {
            serverCtx.clearRect(0, 0, serverCanvas.width, serverCanvas.height);
            const result = wavStreamPlayer.analyser
              ? wavStreamPlayer.getFrequencies('voice')
              : { values: new Float32Array([0]) };
            WavRenderer.drawBars(
              serverCanvas,
              serverCtx,
              result.values,
              '#009900',
              10,
              0,
              8
            );
          }
        }
        frameId = window.requestAnimationFrame(render);
      }
    };
    render();

    return () => {
      isLoaded = false;
      window.cancelAnimationFrame(frameId);
    };
  }, [wavRecorderRef, wavStreamPlayerRef]);

  // Determine if transcript should be visible
  const showInlineTranscript =   items.length > 0 && topProperties?.properties?.length === 0;

  return (
    <div className={`content-actions ${showInlineTranscript ? 'expanded' : ''}`}>
      <div
        style={{
          width: '100%',
          maxWidth: '100%',
          position: 'relative',
          margin: '0 auto',
        }}
      >
        {/* Inline Transcript - shown when no modals are open and no properties are displayed */}
        <InlineTranscript 
          items={items} 
          isVisible={showInlineTranscript}
        />

        <div
          className="visualization"
          style={{
            display:
              status === 'recording' || status === 'idle'
                ? 'flex'
                : 'none',
          }}
        >
          {clientCanvasRef && (
            <div
              className="visualization-entry client"
              style={{ maxWidth: '200px' }}
            >
              <canvas ref={clientCanvasRef} />
            </div>
          )}
        </div>

        {isConnected && !isLoading && (
          <div style={{ position: 'relative' }}>
            {topProperties?.properties?.length !== 0 &&
              status === 'idle' && (
                <p
                  style={{
                    position: 'absolute',
                    top: '-10px',
                    left: '10px',
                    fontSize: '12px',
                  }}
                >
                  Properties: {topProperties?.properties?.length}
                </p>
              )}
            <RecordButton
              status={status}
              setStatus={setStatus}
              loading={load}
              disabled={!isConnected}
              onStart={startRecording}
              onCancel={() => stopRecording(true)}
              onSend={() => {
                stopRecording(true);
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default RecordingSection;
